'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '@components/button';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { routes } from '@routes';
import { zIndex } from '@utils/constants';

import { CookiePreferencesModal } from './cookie-preferences-modal';
import {
  CookieConsentPreferences,
  getStoredConsentPreferences,
  saveConsentPreferences,
  acceptAllCookies,
  rejectAllCookies,
} from './utils';

interface CookieConsentBannerProps {
  className?: string;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  className,
}) => {
  const [isMounted, setIsMounted] = React.useState(false);
  const [hasConsented, setHasConsented] = React.useState(true); // Start true to prevent flash
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] =
    React.useState(false);
  const [storedPreferences, setStoredPreferences] =
    React.useState<CookieConsentPreferences | null>(null);

  // Check consent status on mount
  React.useEffect(() => {
    setIsMounted(true);
    const preferences = getStoredConsentPreferences();
    setStoredPreferences(preferences);
    setHasConsented(preferences !== null);
  }, []);

  const handleAcceptAll = (): void => {
    acceptAllCookies();
    setHasConsented(true);
    // Trigger any analytics/tracking initialization here if needed
    window.dispatchEvent(
      new CustomEvent('cookieConsentUpdated', { detail: { acceptedAll: true } })
    );
  };

  const handleRejectAll = (): void => {
    rejectAllCookies();
    setHasConsented(true);
    window.dispatchEvent(
      new CustomEvent('cookieConsentUpdated', {
        detail: { acceptedAll: false },
      })
    );
  };

  const handleSavePreferences = (
    preferences: Omit<
      CookieConsentPreferences,
      'consentDate' | 'consentVersion'
    >
  ): void => {
    saveConsentPreferences(preferences);
    setHasConsented(true);
    window.dispatchEvent(
      new CustomEvent('cookieConsentUpdated', { detail: preferences })
    );
  };

  const openPreferencesModal = (): void => {
    setIsPreferencesModalOpen(true);
  };

  const closePreferencesModal = (): void => {
    setIsPreferencesModalOpen(false);
  };

  // Don't render anything on server or if user has already consented
  if (!isMounted || hasConsented) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            delay: 0.5, // Small delay so it doesn't appear immediately on page load
          }}
          className={`fixed bottom-0 left-0 right-0 ${className ?? ''}`}
          style={{ zIndex: zIndex.cookieConsent }}
        >
          {/* Gradient backdrop blur effect */}
          <div className="absolute inset-0 bg-gradient-to-t from-off-white via-off-white to-off-white/95 backdrop-blur-sm pointer-events-none" />

          {/* Main content */}
          <div className="relative">
            {/* Decorative top border */}
            <div className="h-1 bg-gradient-to-r from-pool via-primary-main to-secondary-main" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Left side - Icon and text */}
                <div className="flex items-start sm:items-center gap-4 flex-1">
                  {/* Cookie icon with subtle animation */}
                  <motion.div
                    initial={{ rotate: -10 }}
                    animate={{ rotate: 0 }}
                    transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
                    className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-pool flex-shrink-0"
                  >
                    <Icon
                      name={IconNames.LOCK}
                      size="lg"
                      className="text-primary-main"
                    />
                  </motion.div>

                  <div className="flex-1">
                    <Typography
                      variant="subtitle1"
                      variantM="body1"
                      component="h3"
                      className="text-primary-main font-medium mb-1"
                    >
                      We value your privacy
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                      className="text-gray-main"
                    >
                      We use cookies to personalize your experience, improve our
                      service, and show you relevant content. You can manage
                      your preferences anytime. Learn more in our{' '}
                      <Link
                        to={routes.privacy}
                        className="text-primary-main underline hover:text-primary-light transition-colors"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  </div>
                </div>

                {/* Right side - Action buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 lg:flex-shrink-0">
                  {/* Manage Preferences - text button on mobile, outlined on larger screens */}
                  <Button
                    variant="text"
                    onClick={openPreferencesModal}
                    className="order-3 sm:order-1 justify-center text-sm hover:bg-pool/50 px-4 py-2"
                    data-testid="cookie-manage-preferences-button"
                  >
                    <Icon name={IconNames.FILTER} size="sm" className="mr-2" />
                    <span>Manage</span>
                  </Button>

                  {/* Reject All */}
                  <Button
                    variant="outlined"
                    color="gray"
                    onClick={handleRejectAll}
                    className="order-2 justify-center text-sm px-5 py-2.5"
                    data-testid="cookie-reject-all-button"
                  >
                    Reject All
                  </Button>

                  {/* Accept All - Primary CTA */}
                  <Button
                    variant="filled"
                    onClick={handleAcceptAll}
                    className="order-1 sm:order-3 justify-center text-sm px-5 py-2.5 shadow-md hover:shadow-lg transition-shadow"
                    data-testid="cookie-accept-all-button"
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Preferences Modal */}
      <CookiePreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={closePreferencesModal}
        onSave={handleSavePreferences}
        initialPreferences={storedPreferences}
      />
    </>
  );
};

CookieConsentBanner.displayName = 'CookieConsentBanner';
